import { Injectable, inject, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { NATIVE_CALL_PLUGIN } from './native-call-plugin';

/**
 * Native iOS detection and native call service.
 *
 * Everything native-specific in the app branches off isNativeIos.
 * 'web' in any browser → isNativeIos is false → app behaves exactly as today.
 * 'ios' only when running inside the native Capacitor shell (sim-mobile).
 *
 * (learner scope),(mobile CSS), and ERD 3 (screen share)
 * all key off this flag.
 *
 * On an iOS Role Play call the native Daily client owns the learner's mic and
 * camera, so the call controls go through the methods below instead of the
 * WebView. The native side holds the real state; each setter returns it.
 */
@Injectable({ providedIn: 'root' })
export class NativeCallService {
  readonly isNativeIos = Capacitor.isNativePlatform();

  /** True while the native client is in a room. Only ever true on iOS. */
  readonly isCallJoined = signal(false);

  private readonly plugin = inject(NATIVE_CALL_PLUGIN);

  /** Bumped on every leave, so a join that finishes after a leave can't mark the call joined. */
  private leaveCount = 0;

  /** Joins the native client to the room; it publishes camera and mic, with the mic muted. No-op off iOS. */
  async joinCall(url: string, token?: string): Promise<void> {
    if (!this.isNativeIos) return;
    const leaveCountAtJoin = this.leaveCount;
    await this.plugin.join({ url, token });
    if (this.leaveCount === leaveCountAtJoin) {
      this.isCallJoined.set(true);
    }
  }

  /** Leaves the room. No-op off iOS. */
  async leaveCall(): Promise<void> {
    if (!this.isNativeIos) return;
    this.leaveCount++;
    // Cleared before the await so mic/camera changes stop going native right away.
    this.isCallJoined.set(false);
    await this.plugin.leave();
  }

  /** Sets the native mic. Returns the applied state, or null when there is no native call. */
  async setMicrophoneEnabled(enabled: boolean): Promise<boolean | null> {
    if (!this.isCallJoined()) return null;
    const { microphoneEnabled } = await this.plugin.setMicrophoneEnabled({ enabled });
    return microphoneEnabled;
  }

  /** Sets the native camera. Returns the applied state, or null when there is no native call. */
  async setCameraEnabled(enabled: boolean): Promise<boolean | null> {
    if (!this.isCallJoined()) return null;
    const { cameraEnabled } = await this.plugin.setCameraEnabled({ enabled });
    return cameraEnabled;
  }
}
